import { apiUrl } from "../../api/endpoints";
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Spinner from "../../Components/Spinner";

import { LanguageContext } from "../../context/LanguageContext";
import "./UserHealthDetails.css";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Field,
  HealthPageHeader,
  Section,
  SummaryCard,
  boolLabel,
  formatDateTime,
  formatPercent,
  getBMIColor,
  getHealthTranslations,
  getRiskColor,
  isRtlLanguage,
  roundValue,
} from "./userHealthShared";

export const UserHealthDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useContext(LanguageContext);
  const t = getHealthTranslations(lang);
  const isRtl = isRtlLanguage(lang);
  const [data, setData] = useState(null);
  const [reportDownloadMode, setReportDownloadMode] = useState("internal_pdf");
  const [loading, setLoading] = useState(true);
  const label = (key, fallback) => t.labels?.[key] || fallback;
  const section = (key, fallback) => t.sections?.[key] || fallback;
  const returnPath = useMemo(() => {
    if (location.pathname.startsWith("/my-health/details")) return "/my-health";
    if (location.pathname.startsWith("/users-health/details")) {
      const sourceFromQuery = new URLSearchParams(location.search).get("from");
      if (sourceFromQuery === "my-health") return "/my-health";
      return "/users-health";
    }
    const source = new URLSearchParams(location.search).get("from") || location.state?.source;
    if (source === "my-health") return "/my-health";
    if (source === "users-health") return "/users-health";
    if (location.state?.from === "/my-health" || location.state?.from === "/users-health") {
      return location.state.from;
    }
    return "/users-health";
  }, [location.pathname, location.search, location.state]);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await axios.get(apiUrl(`user-health/get/${id}`));
        setData(res.data?.status === "OK" ? res.data.data : null);
      } catch (err) {
        console.error(err);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [id]);

  useEffect(() => {
    const fetchReportConfiguration = async () => {
      try {
        const res = await axios.get(apiUrl(`configuration/public`));
        const mode = res.data?.data?.healthReport?.downloadMode;
        setReportDownloadMode(mode === "anovator_redirect" ? "anovator_redirect" : "internal_pdf");
      } catch (err) {
        console.error("Health report configuration fetch error:", err);
        setReportDownloadMode("internal_pdf");
      }
    };

    fetchReportConfiguration();
  }, []);

  const chartData = useMemo(
    () => [
      { name: t.categories.leftArm, fat: data?.fatLeftArm ?? 0, muscle: data?.muscleLeftArm ?? 0 },
      { name: t.categories.rightArm, fat: data?.fatRightArm ?? 0, muscle: data?.muscleRightArm ?? 0 },
      { name: t.categories.leftLeg, fat: data?.fatLeftLeg ?? 0, muscle: data?.muscleLeftLeg ?? 0 },
      { name: t.categories.rightLeg, fat: data?.fatRightLeg ?? 0, muscle: data?.muscleRightLeg ?? 0 },
      { name: t.categories.trunk, fat: data?.fatTrunk ?? 0, muscle: data?.muscleTrunk ?? 0 },
    ],
    [data, t]
  );

  const insights = useMemo(() => getInsights(data, t), [data, t]);
  const reportLinks = useMemo(() => getAnovatorReportLinks(data?.test_id), [data?.test_id]);
  const pdfReportLink = useMemo(() => apiUrl(`user-health/get/${id}/report.pdf`), [id]);

  if (loading) return <Spinner />;
  if (!data) return <div className="configuration health-page"><div className="health-empty-state">{t.states.noData}</div></div>;

  return (
    <div className="configuration health-page" dir={isRtl ? "rtl" : "ltr"}>
      <div className="health-page-shell">
        <HealthPageHeader
          title={data.user?.name || t.pages.details}
          subtitle={data.user?.email}
          actions={
            <>
              {reportDownloadMode === "internal_pdf" ? (
                <a
                  className="health-secondary-btn health-report-download-btn"
                  href={pdfReportLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {label("downloadReport", "Download Report")}
                </a>
              ) : reportLinks ? (
                <>
                  <a
                    className="health-secondary-btn"
                    href={reportLinks.mobile}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {label("mobileReport", "Mobile Report")}
                  </a>
                  <a
                    className="health-secondary-btn"
                    href={reportLinks.web}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {label("webReport", "Web Report")}
                  </a>
                </>
              ) : null}
              <button
                type="button"
                onClick={() => navigate(returnPath)}
                className="health-back-btn"
              >
                {t.actions.back}
              </button>
            </>
          }
        />

        <div className="health-summary-grid">
          <SummaryCard label={t.columns.bmi} value={roundValue(data.bmi)} color={getBMIColor(data.bmi)} />
          <SummaryCard label={t.labels.avgScore} value={roundValue(data.score)} />
          <SummaryCard label={t.labels.bodyAge} value={roundValue(data.bodyAge)} />
          <SummaryCard label={t.labels.created} value={formatDateTime(data.gmtCreate)} />
        </div>

        <div className="health-grid">
          <Section title={t.sections.basicInfo}>
            <Field label={t.labels.age} value={data.age} />
            <Field label={t.labels.sex} value={data.sex} />
            <Field label={t.labels.bodyAge} value={data.bodyAge} />
            <Field label={t.labels.userEmail} value={data.user?.email} />
          </Section>

          <Section title={t.sections.bodyMetrics}>
            <Field label={t.labels.weight} value={data.weight} />
            <Field label={t.labels.height} value={data.height} />
            <Field label={t.labels.fat} value={formatPercent(data.fat)} />
            <Field label={t.labels.muscle} value={formatPercent(data.muscle)} />
            <Field label={t.labels.water} value={formatPercent(data.water)} />
            <Field label={t.labels.protein} value={formatPercent(data.protein)} />
            <Field label={t.labels.bone} value={roundValue(data.bone)} />
            <Field label={t.labels.subFat} value={formatPercent(data.subFat)} />
            <Field label={t.labels.inFat} value={roundValue(data.inFat)} />
            <Field label={t.labels.waistHipRatio} value={roundValue(data.wc)} />
          </Section>

          <Section title={t.sections.measurements}>
            <Field label={t.labels.waist} value={data.waist} />
            <Field label={t.labels.hips} value={data.hips} />
            <Field label={t.labels.thigh} value={data.thigh} />
            <Field label={t.labels.arm} value={data.armDim} />
            <Field label={t.labels.shank} value={data.shank} />
          </Section>

          <Section title={t.sections.vitals}>
            <Field label={t.labels.maxPressure} value={data.bloodMaxPressure} />
            <Field label={t.labels.minPressure} value={data.bloodMinPressure} />
            <Field label={t.labels.oxygen} value={formatPercent(data.bloodOxygen)} />
            <Field label={t.labels.heartRate} value={data.restingHeartRate} />
          </Section>

          <Section title={t.sections.benefitStats}>
            <Field label={t.labels.bmr} value={roundValue(data.bmr)} />
            <Field label={t.labels.caloriesInput} value={roundValue(data.caloriesInput)} />
            <Field label={t.labels.perfectWeight} value={roundValue(data.perfectWeight)} />
            <Field label={t.labels.weightControl} value={roundValue(data.weightControl)} />
            <Field label={t.labels.fatControl} value={roundValue(data.fatControl)} />
            <Field label={t.labels.muscleControl} value={roundValue(data.muscleControl)} />
            <Field label={t.labels.vitalCapacity} value={roundValue(data.vitalCapacity)} />
          </Section>

          <Section title={t.sections.fitnessGoals}>
            <Field label={t.labels.aerobic} value={data.aerobicGoal} />
            <Field label={t.labels.anaerobic} value={data.anaGoal} />
            <Field label={t.labels.endurance} value={data.enduGoal} />
            <Field label={t.labels.sportGoal} value={data.sportGoal} />
            <Field label={t.labels.agility} value={data.agility} />
            <Field label={t.labels.level} value={data.sportLevel} />
            <Field label={t.labels.safeRisk} value={data.sportSafeRisk} color={getRiskColor(data.sportSafeRisk)} />
          </Section>

          <Section title={section("balanceAndShape", "Balance & Shape")}>
            <Field label={t.labels.balance} value={roundValue(data.balance)} />
            <Field label={t.labels.balanceAngle} value={formatPercent(data.balanceAngle)} />
            <Field label={label("bodyShape", "Body Shape")} value={roundValue(data.bodyShape)} />
            <Field label={label("legRisk", "Leg Risk")} value={data.legRisk} />
          </Section>

          <Section title={t.sections.postureRisks}>
            <Field label={t.labels.humpback} value={data.humpbackRisk} color={getRiskColor(data.humpbackRisk)} />
            <Field label={t.labels.pelvis} value={data.pelvisRisk} color={getRiskColor(data.pelvisRisk)} />
            <Field label={t.labels.shoulder} value={data.shoulderRisk} color={getRiskColor(data.shoulderRisk)} />
            <Field label={t.labels.knee} value={data.kneeRisk} color={getRiskColor(data.kneeRisk)} />
            <Field label={t.labels.spine} value={data.spineRisk} color={getRiskColor(data.spineRisk)} />
            <Field label={t.labels.posture} value={data.postureRisk} color={getRiskColor(data.postureRisk)} />
            <Field label={t.labels.frontHead} value={data.frontHeadRisk} color={getRiskColor(data.frontHeadRisk)} />
          </Section>

          <Section title={t.sections.aiDetection}>
            <Field label={t.labels.frontFullBody} value={boolLabel(data.bodyDetect?.fullBody, t)} />
            <Field label={t.labels.frontRisk} value={data.bodyDetect?.humpbackRisk} />
            <Field label={t.labels.sideFullBody} value={boolLabel(data.sideBodyDetect?.fullBody, t)} />
            <Field label={t.labels.sideRisk} value={data.sideBodyDetect?.humpbackRisk} />
          </Section>

          <Section title={t.sections.extraMeasurements}>
            <Field label={t.labels.head} value={data.head} />
            <Field label={t.labels.neck} value={data.neck} />
            <Field label={t.labels.shoulderWidth} value={data.shoulderWidth} />
            <Field label={t.labels.footLength} value={data.footLength} />
            <Field label={label("occipitalSpace", "Occipital Space")} value={roundValue(data.occipitalSpace)} />
          </Section>

          <Section title={section("segmentFat", "Segment Fat")}>
            <Field label={t.categories.leftArm} value={formatPercent(data.fatLeftArm)} />
            <Field label={t.categories.rightArm} value={formatPercent(data.fatRightArm)} />
            <Field label={t.categories.leftLeg} value={formatPercent(data.fatLeftLeg)} />
            <Field label={t.categories.rightLeg} value={formatPercent(data.fatRightLeg)} />
            <Field label={t.categories.trunk} value={formatPercent(data.fatTrunk)} />
          </Section>

          <Section title={section("segmentMuscle", "Segment Muscle")}>
            <Field label={t.categories.leftArm} value={formatPercent(data.muscleLeftArm)} />
            <Field label={t.categories.rightArm} value={formatPercent(data.muscleRightArm)} />
            <Field label={t.categories.leftLeg} value={formatPercent(data.muscleLeftLeg)} />
            <Field label={t.categories.rightLeg} value={formatPercent(data.muscleRightLeg)} />
            <Field label={t.categories.trunk} value={formatPercent(data.muscleTrunk)} />
          </Section>

          <Section title={t.sections.impedance}>
            <Field label={label("r20LeftArm", "R20 Left Arm")} value={roundValue(data.r20LeftArm)} />
            <Field label={label("r20RightArm", "R20 Right Arm")} value={roundValue(data.r20RightArm)} />
            <Field label={label("r20LeftLeg", "R20 Left Leg")} value={roundValue(data.r20LeftLeg)} />
            <Field label={label("r20RightLeg", "R20 Right Leg")} value={roundValue(data.r20RightLeg)} />
            <Field label={label("r20Trunk", "R20 Trunk")} value={roundValue(data.r20Trunk)} />
            <Field label={label("r100LeftArm", "R100 Left Arm")} value={roundValue(data.r100LeftArm)} />
            <Field label={label("r100RightArm", "R100 Right Arm")} value={roundValue(data.r100RightArm)} />
            <Field label={label("r100LeftLeg", "R100 Left Leg")} value={roundValue(data.r100LeftLeg)} />
            <Field label={label("r100RightLeg", "R100 Right Leg")} value={roundValue(data.r100RightLeg)} />
            <Field label={label("r100Trunk", "R100 Trunk")} value={roundValue(data.r100Trunk)} />
          </Section>

          <section className="health-card health-card-wide">
            <h3>{t.sections.fatVsMuscle}</h3>
          <BodyChart data={chartData} labels={{ fat: t.categories.fat, muscle: t.categories.muscle }} />
          </section>

          <section className="health-card">
            <h3>{t.sections.insights}</h3>
            <div className="health-insights">
              {insights.map((item) => (
                <div key={item} className="insight-item">{item}</div>
              ))}
            </div>
          </section>

          <section className="health-card health-card-full">
            <h3>{t.sections.info}</h3>
            <div className="health-fields">
              <Field label={t.labels.created} value={formatDateTime(data.gmtCreate)} />
              <Field label={label("modified", "Modified")} value={formatDateTime(data.gmtModify)} />
              <Field label={label("testId", "Test ID")} value={data.test_id} />
              <Field label={t.labels.userEmail} value={data.user?.email} />
              <Field label={label("deviceFrom", "Device")} value={data.deviceFrom} />
              <Field label={label("gymName", "Gym")} value={data.gymName} />
              <Field label={label("gymId", "Gym ID")} value={data.gymId} />
            </div>
            <div className="health-images">
              {data.bodyImage ? <img src={`https://www.anovator.com/file/FileAction!loadFile.m?key=${data.bodyImage}`} alt="" /> : null}
              {data.sideImage ? <img src={`https://www.anovator.com/file/FileAction!loadFile.m?key=${data.sideImage}`} alt="" /> : null}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="health-chart-tooltip">
      <div className="health-chart-tooltip-title">{label}</div>
      {payload.map((entry) => (
        <div className="health-chart-tooltip-row" key={entry.dataKey || entry.name}>
          <span className="health-chart-tooltip-dot" style={{ background: entry.color }} />
          <span>{entry.name}</span>
          <strong>{roundValue(entry.value)}</strong>
        </div>
      ))}
    </div>
  );
};

const BodyChart = ({ data, labels }) => (
  <div className="health-chart">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 10, left: -14, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--health-chart-grid)" />
        <XAxis dataKey="name" tick={{ fill: "var(--health-muted-color)", fontSize: "0.75em" }} />
        <YAxis tick={{ fill: "var(--health-muted-color)", fontSize: "0.75em" }} />
        <Tooltip cursor={{ fill: "var(--health-chart-hover)" }} content={<ChartTooltip />} />
        <Legend wrapperStyle={{ color: "var(--health-muted-color)", fontSize: "0.78em" }} />
        <Bar
          dataKey="fat"
          name={labels.fat}
          fill="var(--health-danger-color)"
          radius={[8, 8, 0, 0]}
          activeBar={{ stroke: "var(--health-text-color)", strokeWidth: 1 }}
        />
        <Bar
          dataKey="muscle"
          name={labels.muscle}
          fill="var(--health-success-color)"
          radius={[8, 8, 0, 0]}
          activeBar={{ stroke: "var(--health-text-color)", strokeWidth: 1 }}
        />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const getAnovatorReportLinks = (testId) => {
  const normalizedTestId = String(testId || "").trim();
  if (!normalizedTestId) return null;

  const encodedTestId = encodeURIComponent(normalizedTestId);
  return {
    mobile: `https://www.anovator.com/body/mobile8.0.html?id=${encodedTestId}&lang=en_EN&unit=metric`,
    web: `https://www.anovator.com/report/index.html?id=${encodedTestId}&lang=en_EN&unit=metric`,
  };
};

const getInsights = (data, t) => {
  if (!data) return [t.states.goodCondition];

  const insights = [];
  if (Number(data.bmi) > 30) insights.push(t.insights.highBmi);
  if (Number(data.fatTrunk) > 12) insights.push(t.insights.highTrunkFat);
  if (Number(data.restingHeartRate) > 100) insights.push(t.insights.highHeartRate);
  if (Number(data.postureRisk) >= 4) insights.push(t.insights.poorPosture);
  return insights.length ? insights : [t.states.goodCondition];
};
